import * as React from 'react';
import { Check, Eye, Info, Sparkles } from 'lucide-react';
import { AreaChip } from '@/components/ui/area-chip';
import {
  Callout,
  PartLabel,
  Step,
  Steps,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import {
  addInterviewPerson,
  cancelInterview,
  markInterviewHeld,
  scheduleInterview,
  setInterviewOptional,
  useDiscoveryWrite,
  usePeopleQuery,
  type InterviewDetail,
} from '@/lib/api/discovery-sessions';
import { cn } from '@/lib/utils/cn';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { DropZone, NotesEntry, useTranscriptUpload } from '../transcript-review/transcript-step';
import { errorText } from './errors';
import { QuestionnaireStep } from './questionnaire-step';
import { formatWhen, localInputValue, longDay, toIsoInstant } from './sessions-logic';

/**
 * One interview before the session (design note section 3): three steps,
 * only the current one open. Who and when (the person, the date, the
 * invite; on a plan row with no one named, "Who will you interview?" with
 * "Mark it optional" as the way out), what to ask (the questions under their
 * area, two per area then "Show N more", the on-screen asks, the tip said
 * once), and after the session (one drop zone: uploading marks it held; or
 * type the notes; or mark it held). Cancel stays a link at the foot.
 */
export function BeforeSession({
  engagementId,
  d,
  canExtend,
  onOpenPerson,
}: {
  engagementId: string;
  d: InterviewDetail;
  canExtend: boolean;
  onOpenPerson: (personId: string) => void;
}) {
  const named = d.people.length > 0;
  const [open, setOpen] = React.useState<
    Partial<Record<'who' | 'ask' | 'quest' | 'after', boolean>>
  >({});
  const isOpen = (k: 'who' | 'ask' | 'quest' | 'after', def: boolean) => open[k] ?? def;
  const toggle = (k: 'who' | 'ask' | 'quest' | 'after', def: boolean) =>
    setOpen((o) => ({ ...o, [k]: !isOpen(k, def) }));
  const i = d.interview;
  const qn = d.questionnaire;
  const asked = d.questions.filter((q) => q.included).length;

  return (
    <>
      <Steps>
        <Step
          n={1}
          title={named ? 'Who and when' : 'Who will you interview?'}
          state={named ? 'done' : 'current'}
          summary={
            named
              ? `${d.people.map((p) => p.full_name).join(', ')}${i.scheduled_at ? ` · ${formatWhen(i.scheduled_at)}` : ' · no date yet'}`
              : 'No one named yet'
          }
          open={isOpen('who', !named || !i.scheduled_at)}
          onToggle={() => toggle('who', !named || !i.scheduled_at)}
        >
          {named ? (
            <WhoAndWhen engagementId={engagementId} d={d} onOpenPerson={onOpenPerson} />
          ) : (
            <NameSomeone engagementId={engagementId} d={d} />
          )}
        </Step>
        <Step
          n={2}
          title="What to ask"
          state={named ? 'current' : 'todo'}
          summary={`${plural(asked, 'question')} in ${plural(d.areas.length, 'area')}`}
          open={isOpen('ask', named && !!i.scheduled_at)}
          onToggle={() => toggle('ask', named && !!i.scheduled_at)}
        >
          <WhatToAsk d={d} />
        </Step>
        {qn ? (
          <Step
            n={3}
            title={`Questionnaire · ${qn.name}`}
            state={qn.answered >= qn.total ? 'done' : 'todo'}
            summary={`${qn.answered} of ${qn.total} answered`}
            open={isOpen('quest', false)}
            onToggle={() => toggle('quest', false)}
          >
            <QuestionnaireStep engagementId={engagementId} d={d} canExtend={canExtend} />
          </Step>
        ) : null}
        <Step
          n={qn ? 4 : 3}
          title="After the session"
          state="todo"
          summary="Upload the transcript; it marks the interview held"
          open={isOpen('after', false)}
          onToggle={() => toggle('after', false)}
        >
          <AfterTheSession engagementId={engagementId} d={d} />
        </Step>
      </Steps>
      <CancelLink engagementId={engagementId} d={d} />
    </>
  );
}

function WhoAndWhen({
  engagementId,
  d,
  onOpenPerson,
}: {
  engagementId: string;
  d: InterviewDetail;
  onOpenPerson: (personId: string) => void;
}) {
  const i = d.interview;
  const { toast } = useToast();
  const [when, setWhen] = React.useState(localInputValue(i.scheduled_at));
  const schedule = useDiscoveryWrite(engagementId, (at: string) =>
    scheduleInterview(engagementId, i.id, { scheduled_at: at, send_invite: true }),
  );
  const person = d.people[0];
  const onSchedule = () => {
    const at = toIsoInstant(when);
    if (!at) return;
    schedule.mutate(at, {
      onSuccess: (r) =>
        toast({
          message: `${i.code} scheduled for ${formatWhen(r.scheduled_at)}. ${
            r.invites_sent > 0
              ? `Invite sent to ${person?.full_name ?? 'the interviewee'}.`
              : person
                ? `No invite went: add ${person.full_name}'s email in People to send one.`
                : 'Add who you are interviewing to send the invite.'
          }`,
        }),
      onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
    });
  };
  return (
    <>
      <div className="flex flex-wrap gap-4">
        {d.people.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpenPerson(p.id)}
            title="Open in People"
            className="flex items-center gap-2 rounded-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
          >
            <PersonAvatar id={p.id} name={p.full_name} size={28} />
            <span>
              <span className="block text-body-sm font-medium text-ink-900">{p.full_name}</span>
              <span className="block text-meta text-ink-500">
                {[p.title, p.email].filter(Boolean).join(' · ') || 'No title or email yet'}
              </span>
            </span>
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-2.5">
        <WorkspaceField label="When" className="w-[230px]">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          )}
        </WorkspaceField>
        <WorkspaceButton
          size="md"
          variant={i.status === 'scheduled' ? 'secondary' : 'primary'}
          disabled={!toIsoInstant(when) || schedule.isPending}
          onClick={onSchedule}
        >
          {i.status === 'scheduled' ? 'Update and resend invite' : 'Schedule and send invite'}
        </WorkspaceButton>
        {i.invite ? (
          <span className="pb-2 text-meta text-ink-500">
            Invite sent {longDay(i.invite.sent_at)} to {i.invite.recipients.join(', ')}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** A plan row with no one named: choose who from People, or mark it optional. */
function NameSomeone({ engagementId, d }: { engagementId: string; d: InterviewDetail }) {
  const { toast } = useToast();
  const people = usePeopleQuery(engagementId);
  const [pick, setPick] = React.useState('');
  const add = useDiscoveryWrite(engagementId, (personId: string) =>
    addInterviewPerson(engagementId, d.interview.id, personId),
  );
  const optional = useDiscoveryWrite(engagementId, () =>
    setInterviewOptional(engagementId, d.interview.id, true),
  );
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });
  const active = (people.data?.items ?? []).filter(
    (p) => p.status !== 'retired' && !p.possible_duplicate_of,
  );
  return (
    <>
      <div className="flex flex-wrap items-end gap-2.5">
        <WorkspaceField label="Who will you interview?" className="w-[300px]">
          {(id) => (
            <WorkspaceSelect id={id} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Choose from People</option>
              {active.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                  {p.title ? `, ${p.title}` : ''}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
        <WorkspaceButton
          variant="primary"
          size="md"
          disabled={!pick || add.isPending}
          onClick={() =>
            add.mutate(pick, {
              onSuccess: () =>
                toast({
                  message: `${active.find((p) => p.id === pick)?.full_name ?? 'They'} will be interviewed in ${d.interview.code}. Schedule it to send the invite.`,
                }),
              onError: fail,
            })
          }
        >
          Name them
        </WorkspaceButton>
      </div>
      <div className="mt-3 text-meta text-ink-500">
        Someone new? Add them in People first.{' '}
        {!d.interview.optional ? (
          <>
            Not needed for this engagement?{' '}
            <button
              type="button"
              className={linkishClass()}
              disabled={optional.isPending}
              onClick={() =>
                optional.mutate(undefined, {
                  onSuccess: () =>
                    toast({
                      message: `${d.interview.code} is optional: it leaves the plan count and stays on record with its code.`,
                    }),
                  onError: fail,
                })
              }
            >
              Mark it optional
            </button>
          </>
        ) : (
          'It is optional: it does not count in the plan.'
        )}
      </div>
    </>
  );
}

/**
 * The questions under their area chip, the first two per area then "Show N
 * more"; the questions of no area (the type's own, custom) under "General";
 * then the on-screen asks. With `readOnly` (the folded plan of a held
 * interview) every question shows and the tip is not repeated.
 */
export function WhatToAsk({ d, readOnly = false }: { d: InterviewDetail; readOnly?: boolean }) {
  const [more, setMore] = React.useState<Set<string>>(new Set());
  const questions = d.questions.filter((q) => q.included);
  const demos = d.demos.filter((x) => x.included);
  const groups = [
    ...d.areas.map((a) => ({
      key: a.id,
      label: a.label,
      items: questions.filter((q) => q.area_id === a.id),
    })),
    {
      key: 'general',
      label: 'General',
      items: questions.filter((q) => !q.area_id || !d.areas.some((a) => a.id === q.area_id)),
    },
  ].filter((g) => g.items.length > 0);
  return (
    <>
      {groups.length === 0 ? <div className="text-meta text-ink-500">No questions.</div> : null}
      {groups.map((g, gi) => {
        const all = readOnly || more.has(g.key);
        const shown = all ? g.items : g.items.slice(0, 2);
        return (
          <div key={g.key} className={cn(gi > 0 && 'mt-3.5')}>
            <div className="mb-1 flex items-center gap-2">
              {g.key === 'general' ? (
                <span className="text-meta font-medium text-ink-700">General</span>
              ) : (
                <AreaChip>{g.label}</AreaChip>
              )}
              <span className="text-meta text-ink-500">{plural(g.items.length, 'question')}</span>
            </div>
            <ul className="m-0 list-none p-0">
              {shown.map((q, n) => (
                <li
                  key={q.id}
                  className="flex items-start gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900 last:border-b-0"
                >
                  <span className="w-[18px] flex-none font-mono text-[11px] leading-5 text-ink-300">
                    {n + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    {q.text}
                    {q.ai_reason ? (
                      <span className="mt-0.5 flex items-center gap-1 text-meta text-ai-ink">
                        <Sparkles className="h-3 w-3" aria-hidden="true" /> {q.ai_reason}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
            {!all && g.items.length > 2 ? (
              <button
                type="button"
                className={linkishClass()}
                onClick={() => setMore((s) => new Set(s).add(g.key))}
              >
                Show {g.items.length - 2} more
              </button>
            ) : null}
          </div>
        );
      })}
      {demos.length > 0 ? (
        <>
          <PartLabel className="mt-3.5">Ask to see on screen</PartLabel>
          <ul className="m-0 list-none p-0">
            {demos.map((x) => (
              <li
                key={x.id}
                className="flex items-start gap-2.5 border-b border-ink-100 py-[7px] text-body-sm text-ink-900 last:border-b-0"
              >
                <Eye className="mt-[3px] h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
                {x.text}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {readOnly ? null : (
        <Callout tone="tip" icon={Info} className="mt-3">
          Say the subject out loud when you change it (“let’s talk about bank files”). Caliber uses
          it to tag the transcript.
        </Callout>
      )}
    </>
  );
}

function AfterTheSession({ engagementId, d }: { engagementId: string; d: InterviewDetail }) {
  const { toast } = useToast();
  const upload = useTranscriptUpload(engagementId, d, null);
  const markHeld = useDiscoveryWrite(engagementId, () =>
    markInterviewHeld(engagementId, d.interview.id),
  );
  if (upload.busy) return <>{upload.progress}</>;
  return (
    <>
      {upload.input}
      <DropZone held={false} onPick={upload.pick} />
      <div className="mt-3 flex items-center gap-2">
        <NotesEntry onSave={upload.saveNotes} saving={upload.savingNotes} />
        <span className="flex-1" />
        {d.interview.status === 'scheduled' ? (
          <WorkspaceButton
            disabled={markHeld.isPending}
            onClick={() =>
              markHeld.mutate(undefined, {
                onSuccess: () =>
                  toast({
                    message: `${d.interview.code} marked as held. Upload the transcript when it arrives.`,
                  }),
                onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
              })
            }
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Mark as held
          </WorkspaceButton>
        ) : null}
      </div>
    </>
  );
}

/** Cancel with a reason (Decision 54.15): the code stays on record. */
function CancelLink({ engagementId, d }: { engagementId: string; d: InterviewDetail }) {
  const i = d.interview;
  const { toast } = useToast();
  const [cancelling, setCancelling] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const cancel = useDiscoveryWrite(engagementId, (why: string) =>
    cancelInterview(engagementId, i.id, why),
  );
  const person = d.people[0];
  const inviteSent = !!i.invite;
  if (!cancelling) {
    return (
      <button
        type="button"
        className={cn(linkishClass(true), 'mt-1')}
        onClick={() => setCancelling(true)}
      >
        Cancel interview…
      </button>
    );
  }
  return (
    <Callout tone="form" className="mt-3.5">
      <div className="font-semibold text-ink-900">Cancel {i.code}</div>
      <div className="mb-2 text-meta text-ink-500">
        It keeps its code and stays on record.{' '}
        {inviteSent && person
          ? `${person.full_name} is sent a cancellation.`
          : 'No invite was sent, so nobody is told.'}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceInput
          aria-label="Why it won't happen"
          placeholder="Why it won’t happen"
          autoComplete="off"
          className="max-w-[420px] flex-1"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <WorkspaceButton
          variant="destructive"
          disabled={!reason.trim() || cancel.isPending}
          onClick={() =>
            cancel.mutate(reason.trim(), {
              onSuccess: () =>
                toast({
                  message: `${i.code} cancelled. It keeps its code and stays on record.${
                    inviteSent && person ? ` A cancellation went to ${person.full_name}.` : ''
                  }`,
                }),
              onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
            })
          }
        >
          Cancel interview
        </WorkspaceButton>
        <button
          type="button"
          className={linkishClass()}
          onClick={() => {
            setCancelling(false);
            setReason('');
          }}
        >
          Keep it
        </button>
      </div>
    </Callout>
  );
}
